import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

interface NavigationContextType {
  isNavigating: boolean;
  isInitialLoad: boolean;
  startNavigation: () => void;
  completeNavigation: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

// If `vike:pageTransitionEnd` never fires (e.g. a hung/errored client-side navigation), this
// bounds how long isNavigating can stay stuck true so nav-progress UI doesn't spin forever.
const NAVIGATION_STUCK_TIMEOUT_MS = 15_000;

export function NavigationProvider({ children }: { children: React.ReactNode }) {
  const [isNavigating, setIsNavigating] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // Use ref to track initial load so event handlers always have current value
  const isInitialLoadRef = useRef(true);
  const stuckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mark initial load as complete after first render
  useEffect(() => {
    // Check if this is truly the initial load by checking navigation state
    // If __vikeHasNavigated exists, this is not the initial load
    const hasNavigatedBefore = typeof window !== 'undefined' && (window as any).__vikeHasNavigated === true;
    
    if (hasNavigatedBefore) {
      // Not initial load - mark immediately
      isInitialLoadRef.current = false;
      setIsInitialLoad(false);
    } else {
      // Small delay to ensure we've hydrated and this is truly the initial load
      const timeout = setTimeout(() => {
        isInitialLoadRef.current = false;
        setIsInitialLoad(false);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, []);

  // Listen to Vike's page transition events
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const clearStuckTimeout = () => {
      if (stuckTimeoutRef.current !== null) {
        clearTimeout(stuckTimeoutRef.current);
        stuckTimeoutRef.current = null;
      }
    };

    const handleTransitionStart = () => {
      // Don't show navigation progress on initial SSR page load
      // Use ref to get current value (not stale closure)
      if (isInitialLoadRef.current) {
        return;
      }
      setIsNavigating(true);
      clearStuckTimeout();
      stuckTimeoutRef.current = setTimeout(() => {
        setIsNavigating(false);
      }, NAVIGATION_STUCK_TIMEOUT_MS);
    };

    const handleTransitionEnd = () => {
      clearStuckTimeout();
      setIsNavigating(false);
    };

    window.addEventListener('vike:pageTransitionStart', handleTransitionStart);
    window.addEventListener('vike:pageTransitionEnd', handleTransitionEnd);

    return () => {
      clearStuckTimeout();
      window.removeEventListener('vike:pageTransitionStart', handleTransitionStart);
      window.removeEventListener('vike:pageTransitionEnd', handleTransitionEnd);
    };
  }, []); // Empty deps - we use ref instead

  const startNavigation = useCallback(() => {
    setIsNavigating(true);
  }, []);

  const completeNavigation = useCallback(() => {
    setIsNavigating(false);
  }, []);

  return (
    <NavigationContext.Provider value={{ isNavigating, isInitialLoad, startNavigation, completeNavigation }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
}

/** Safe version that returns null when outside NavigationProvider. Use when caller may render outside the provider so hook order stays consistent. */
export function useNavigationOptional(): NavigationContextType | null {
  return useContext(NavigationContext) ?? null;
}

